import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/store/AuthStore";
import BrandLogo from "@/components/layout/BrandLogo";
import FullLoader from "@/components/base/FullLoader";
import LoginForm from "@/pages/auth/components/LoginForm";
import RegisterForm from "@/pages/auth/components/RegisterForm";

type Tab = "login" | "register";

const AuthPage = () => {
  const { session, profile, loading } = useAuth();
  const [tab, setTab] = useState<Tab>("login");

  if (loading) return <FullLoader label="Cargando plataforma..." />;
  if (session) {
    return <Navigate to={profile?.role === "asesor" ? "/asesor" : "/admin"} replace />;
  }

  return (
    <div className="attd-app-bg min-h-screen w-full">
      <div className="mx-auto max-w-[1180px] px-4 md:px-6 py-8 md:py-12 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 w-full items-center">
          {/* Panel de marca */}
          <div className="animate-fade-up">
            <BrandLogo />
            <h1 className="editable-title text-3xl md:text-5xl font-extrabold tracking-tight mt-8 leading-tight">
              Ecosistema de Gestión Comercial
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mt-4 max-w-md">
              Amway Tienda Digital Domicilios: ventas, inventario en consignación, red de
              distribuidores y cartera, todo en un solo tablero con datos protegidos en la nube.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-8 max-w-md">
              {[
                { icon: "ri-line-chart-line", label: "KPIs ejecutivos" },
                { icon: "ri-team-line", label: "Red de asesores" },
                { icon: "ri-archive-2-line", label: "Inventario consolidado" },
                { icon: "ri-database-2-line", label: "Datos en la nube" },
              ].map((f) => (
                <div
                  key={f.label}
                  className="glass-panel rounded-lg px-3.5 py-3 flex items-center gap-2.5"
                >
                  <span className="w-8 h-8 rounded-md bg-primary-500/25 text-accent-200 flex items-center justify-center shrink-0">
                    <i className={`${f.icon} text-base`} />
                  </span>
                  <span className="text-xs font-semibold text-foreground-800">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tarjeta de acceso */}
          <div className="glass-panel-strong rounded-xl p-5 md:p-7 border-accent-500/25 animate-fade-up">
            <div className="flex items-center bg-background-200/60 rounded-full p-1 mb-6">
              {[
                { key: "login" as Tab, label: "Iniciar sesión", icon: "ri-login-box-line" },
                { key: "register" as Tab, label: "Registrarme", icon: "ri-user-add-line" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    tab === t.key
                      ? "bg-primary-500 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <i className={t.icon} />
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mb-5">
              <h2 className="editable-title text-xl font-bold">
                {tab === "login" ? "Bienvenido de vuelta" : "Crear cuenta de asesor"}
              </h2>
              <p className="text-xs text-foreground-600 mt-1">
                {tab === "login"
                  ? "Ingresa con tu correo y contraseña para acceder a tu panel."
                  : "Completa tus datos para unirte a la red de distribuidores."}
              </p>
            </div>

            {tab === "login" ? (
              <LoginForm onSwitch={() => setTab("register")} />
            ) : (
              <RegisterForm onSwitch={() => setTab("login")} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;